import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";

import { isAuthFreePath, LOGIN_PATH, PRICING_PATH } from "@/lib/auth/auth-paths";
import { getApiError } from "@/lib/api/errors";
import {
  getAccessToken,
  isJwtExpired,
  markDevLoggedOut,
  TokenError,
} from "@/lib/auth/token-provider";
import { storage } from "@/lib/storage";
import type { PaginatedResponse } from "@/types/api";

export const USER_ENDPOINT = "/dashboard/user";

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// serviceId: undefined sends the selected service from localStorage as
// X-Service-Id, null omits the header, a string sends that id.
declare module "axios" {
  interface AxiosRequestConfig {
    serviceId?: string | null;
    skipAuth?: boolean;
  }
}

api.interceptors.request.use(async (config) => {
  if (!config.skipAuth) {
    try {
      const token = await getAccessToken();
      config.headers.Authorization = `JWT ${token}`;
    } catch (error) {
      if (error instanceof TokenError) {
        console.warn("Token retrieval failed:", error.message);
      }
      throw error;
    }
  }

  if (config.serviceId === undefined) {
    const selectedServiceId = storage.getSelectedServiceId();
    if (selectedServiceId) {
      config.headers.set("X-Service-Id", selectedServiceId);
    }
  } else if (config.serviceId !== null) {
    config.headers.set("X-Service-Id", config.serviceId);
  }

  return config;
});

/**
 * Only for redirects triggered by an API signal (401/403/404/402 here, unreachable
 * API in providers.tsx). The full reload is intentional: it resets all
 * in-memory state, including a half-finished session. Auth-STATE-driven
 * redirects go through the guards' <Navigate> instead.
 */
export function hardRedirect(url: string): void {
  if (typeof window === "undefined") return;
  window.location.assign(url);
}

/**
 * A rejected token answers 401. A 403 is the legitimate "you don't own this
 * service" answer (or a credential that isn't a JWT), so a 403 only ends the
 * session when our own copy of the token agrees it is finished; an ownership
 * 403 on a live token is left to the caller.
 */
function hasDeadToken(): boolean {
  const token = storage.getAuthToken();
  return !token || isJwtExpired(token);
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (typeof window === "undefined") {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    if (status === 401 || (status === 403 && hasDeadToken())) {
      console.warn(`${status} - session rejected, clearing session`);
      storage.clearAuthSession();
      if (!isAuthFreePath(window.location.pathname)) {
        // Only alongside the redirect: a 401 from an auth-free path is a failed
        // sign-in attempt (a rejected Google credential, say), not a session
        // that died under the user, and must not claim otherwise on /login.
        storage.markSessionExpired();
        hardRedirect(LOGIN_PATH);
      }
    } else if (status === 404 && error.config?.url === USER_ENDPOINT) {
      // The token is valid but its user no longer exists, so it can never work
      // again: sign out. No "session expired" notice, which would promise to
      // pick up where the user left off.
      console.warn("404 - user not found, clearing session");
      storage.clearAuthSession();
      // Dev-only: stop the VITE_DEV_AUTH_JWT seed from restoring the same
      // token on the reload below and looping (no-op in production).
      markDevLoggedOut();
      if (!isAuthFreePath(window.location.pathname)) {
        hardRedirect(LOGIN_PATH);
      }
    } else if (status === 402) {
      const url = getApiError(error).data?.url;
      const pricingUrl = typeof url === "string" ? url : PRICING_PATH;
      // Unlike a dead session, a 402 must redirect even on auth-free paths: the
      // paywall can fire while the user is still on one (a data-source or
      // notification action retried from /onboarding-done, say). Guard against
      // the page we're already on (by path or full URL) to avoid a reload loop.
      const alreadyThere =
        window.location.pathname === PRICING_PATH ||
        window.location.href === pricingUrl;
      if (!alreadyThere) {
        hardRedirect(pricingUrl);
      }
    }
    return Promise.reject(error);
  }
);

/** Safety valve so a server that keeps handing back a `next` cursor can't spin forever. */
const MAX_PAGES = 100;

/**
 * List endpoints cap a response at `limit` items (default 50, above 100 treated
 * as 100), so every list must walk `pagination.next` (a path and query relative
 * to the API, resolved against baseURL) to the end.
 *
 * `params` apply to the first request only: the `next` URL already carries
 * the query it was issued with, so re-appending them would duplicate them.
 */
export async function getAllPages<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T[]> {
  const items: T[] = [];
  let next: string | null = url;
  let requestConfig = config;

  for (let page = 0; next && page < MAX_PAGES; page += 1) {
    const response: AxiosResponse<PaginatedResponse<T>> =
      await api.get<PaginatedResponse<T>>(next, requestConfig);
    items.push(...response.data.data);
    next = response.data.pagination?.next ?? null;
    requestConfig = config ? { ...config, params: undefined } : undefined;
  }

  return items;
}

export function isApiUnreachableError(error: unknown): boolean {
  return error instanceof AxiosError && !error.response && !axios.isCancel(error);
}

/**
 * Never retries an unreachable API (retries only delay the internal-error
 * page) or a pre-flight TokenError (can't succeed without a token; the guard
 * routes to /login).
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof TokenError || isApiUnreachableError(error)) {
    return false;
  }
  return failureCount < 3;
}

export function retryUnlessNotFound(
  failureCount: number,
  error: unknown
): boolean {
  if (error instanceof AxiosError && error.response?.status === 404) {
    return false;
  }
  return shouldRetryQuery(failureCount, error);
}
